import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import BlockIcon from '@mui/icons-material/Block';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseFullscreenRounded from '@mui/icons-material/CloseFullscreenRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import FileCopyRoundedIcon from '@mui/icons-material/FileCopyRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import ImageNotSupportedRoundedIcon from '@mui/icons-material/ImageNotSupportedRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PendingIcon from '@mui/icons-material/Pending';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import RemoveIcon from '@mui/icons-material/Remove';
import RemoveCircleRoundedIcon from '@mui/icons-material/RemoveCircleRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import ReviewsRoundedIcon from '@mui/icons-material/ReviewsRounded';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { SvgIcon, SvgIconProps, useTheme } from '@mui/material';
import { TIconName, StatusColor } from './constants';

const ICON_MAPPING: { [iconName in TIconName]: typeof SvgIcon } = {
  checkCircle: CheckCircleIcon,
  block: BlockIcon,
  remove: RemoveIcon,
  pending: PendingIcon,
  checkCircleRounded: CheckCircleRoundedIcon,
  warningRounded: WarningRoundedIcon,
  cancelRounded: CancelRoundedIcon,
  blockRounded: BlockRoundedIcon,
  radioButtonUncheckedRounded: RadioButtonUncheckedRoundedIcon,
  removeCircleRounded: RemoveCircleRoundedIcon,
  errorRounded: ErrorRoundedIcon,
  keyboardArrowDownRounded: KeyboardArrowDownRoundedIcon,
  deleteRounded: DeleteRoundedIcon,
  warningAmberRounded: WarningAmberRoundedIcon,
  addRounded: AddRoundedIcon,
  closeRounded: CloseRoundedIcon,
  editRounded: EditRoundedIcon,
  verifiedRounded: VerifiedRoundedIcon,
  arrowRightRounded: KeyboardArrowRightRoundedIcon,
  arrowLeftRounded: KeyboardArrowLeftRoundedIcon,
  LockRounded: LockRoundedIcon,
  emailRounded: EmailRoundedIcon,
  moreVertRounded: MoreVertRoundedIcon,
  closeFullscreenRounded: CloseFullscreenRounded,
  autoAwesomeRounded: AutoAwesomeRounded,
  reviewsRounded: ReviewsRoundedIcon,
  fileCopyRounded: FileCopyRoundedIcon,
  uploadFileIcon: UploadFileIcon,
  fileDownloadRounded: FileDownloadRoundedIcon,
  imageNotSupportedRounded: ImageNotSupportedRoundedIcon,
  openInNewRounded: OpenInNewRoundedIcon,
  pdfRounded: PictureAsPdfRoundedIcon,
  replayRounded: ReplayRoundedIcon,
  infoRounded: InfoRoundedIcon,
  checkRounded: CheckRoundedIcon,
};

/**
 * ColoredIcon - Displays icons with automatic semantic color handling.
 *
 * This component automatically calculates the correct fill color based on context (standalone vs inside chip)
 * using the StatusColor object from theme palette hooks.
 *
 * ## Basic Usage
 *
 * **Standalone icon (default):**
 * ```tsx
 * const color = useStatusColor('success');
 * <ColoredIcon iconName="checkCircle" statusColor={color} />
 * // Automatically uses: fill || icon (main color, e.g., green #4caf50)
 * ```
 *
 * **Icon inside ColoredChip (automatic):**
 * ```tsx
 * const color = useStatusColor('success');
 * <ColoredChip statusColor={color}>
 *   <ColoredIcon iconName="checkCircle" />
 * </ColoredChip>
 * // ColoredChip auto-injects: variant="in-chip" and statusColor
 * // Icon automatically uses: icon || text (contrasting white on green background)
 * ```
 *
 * ## Props
 *
 * @param iconName - The icon to display (see TIconName in constants.ts)
 * @param statusColor - StatusColor object from theme hooks (useStatusColor, useEntityColor, etc.)
 * @param variant - Context for fill color calculation:
 *   - `'standalone'` (default): Main color for icons on neutral backgrounds (fill || icon)
 *   - `'in-chip'`: Contrasting color for icons inside colored chips (icon || text)
 * @param iconSize - MUI icon size: 'small' | 'medium' | 'large' | 'inherit'
 * @param isCompact - Whether to use compact sizing
 * @param sx - Additional MUI sx styles (can override fill color if needed)
 *
 * ## Manual Override
 *
 * For custom color logic, use the `sx` prop:
 * ```tsx
 * <ColoredIcon iconName="checkCircle" sx={{ fill: customColor }} />
 * ```
 *
 * @see ColoredChip for automatic prop injection when icons are used inside chips
 * @see StatusColor type in constants.ts for color object structure
 */
export default function ColoredIcon({
  iconName,
  iconSize = 'small',
  sx,
  isCompact,
  statusColor,
  variant = 'standalone',
  ...props
}: {
  iconName: TIconName;
  iconSize?: 'small' | 'inherit' | 'large' | 'medium';
  isCompact?: boolean;
  statusColor?: StatusColor;
  variant?: 'standalone' | 'in-chip';
} & SvgIconProps) {
  const theme = useTheme();
  const Icon = ICON_MAPPING[iconName];

  // Determine fill color based on statusColor and variant
  let fillColor: string | undefined = undefined;
  if (statusColor) {
    fillColor =
      variant === 'in-chip'
        ? statusColor.icon || statusColor.text // Contrasting color for chips
        : statusColor.fill || statusColor.icon; // Main color for standalone
  } else if (props.color) {
    // Legacy: Extract color from MUI color prop if provided
    const colorMap: Record<string, string> = {
      success: theme.palette.success.main,
      error: theme.palette.error.main,
      warning: theme.palette.warning.main,
      info: theme.palette.info.main,
      disabled: theme.palette.action.disabled,
      primary: theme.palette.primary.main,
      secondary: theme.palette.secondary.main,
    };
    fillColor = colorMap[props.color];
  }

  return (
    <Icon
      sx={{
        alignSelf: 'center',
        flex: 'auto',
        fill: fillColor,
        ...sx,
      }}
      fontSize={iconSize}
      {...props}
    />
  );
}
